import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { BaseBreadcrumbWithRender } from '@app/components/common/BaseBreadcrumb/BaseBreadcrumb';
import { PageTitle } from '@app/components/common/PageTitle/PageTitle';
import { LabReportTable } from '@app/components/apps/Reports/LabReports/LabReportLayout';
import { LabFlatReportTable } from '@app/components/apps/Reports/LabReports/LabFlatReportLayout';
import ReportFilterBar from './ReportFilterBar';
import { message, Space } from 'antd';
import { DownloadOutlined, FileExcelOutlined, EditOutlined } from '@ant-design/icons';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { PRLicenseReportTable } from '@app/components/apps/Reports/PRReports/PRReportLayout';
import { PREstablishmentReportTable } from '@app/components/apps/Reports/PRReports/PREstReportLayout';
import ImportExportReportTable from '@app/components/apps/Reports/ImpExpReports/ImportExportEarningsReport';
import ImportExportComparisonLayout from '@app/components/apps/Reports/ImpExpReports/ImpExpComparisonReport';
import ImportExportNominalEarnings from '@app/components/apps/Reports/ImpExpReports/ImpExpNominalEarningsReport';
import ImportExportNominalCount from '@app/components/apps/Reports/ImpExpReports/ImpExpNominalCountsReport';
import ImpExpLevelQtyReport from '@app/components/apps/Reports/ImpExpReports/ImpExpLevelsbyQtyReport';
import ConsignmentByCountryReport from '@app/components/apps/Reports/ImpExpReports/ConsignmentByCountryReport';
import TonnageReport from '@app/components/apps/Reports/ImpExpReports/TonnageReport';
import HReport from '@app/components/apps/Reports/FinanceReports/HReport';
import SummaryReport from '@app/components/apps/Reports/FinanceReports/SummaryReport';
import CPSummaryReport from '@app/components/apps/Reports/FinanceReports/CPSummaryReport';
import { convertReportToCSV } from '@app/utils/ConverttoCSV';

const breadcrumbMap: Record<string, { name: string; path: string }> = {
  lab: { name: 'Lab Reports', path: '/lab-reports' },
  'pr&license': { name: 'PR & License Reports', path: '/pr-license-reports' },
  'import&export': { name: 'Import & Export Reports', path: '/import-export-reports' },
  finance: { name: 'Financial Reports', path: '/finance-reports' },
  followup: { name: 'Follow-up Reports', path: '/followup-reports' },
};

// Title Mapping
const titleMap: Record<string, string> = {
  'earnings-by-divisions': 'Lab - Earnings by Divisions',
  'test-count-by-divisions': 'Lab - Test Count by Divisions',
  'sample-count-by-divisions': 'Lab - Sample Count by Divisions',
  'item-qty-by-divisions': 'Lab - Item Quantity by Divisions',
  'earnings-by-divisions-nom-pay': 'Lab - Earnings by Divisions For Nominal Payments',
  'test-count-by-divisions-nom-pay': 'Test Count by Divisions For Nominal Payments',
  'pr-earnings-by-departments': 'PR & License - Earnings by Departments',
  'pr-count-by-departments': 'PR & License - Registration Count by Departments',
  'registrations-by-establishment': 'Registrations by Establishment',
  'imp-exp-earnings-by-departments': 'Import & Export - Earnings by Department',
  'imp-exp-count-by-departments': 'Import & Export - Task Count by Department',
  'imp-exp-earnings-comparison': 'Import & Export - Earnings Comparison',
  'imp-exp-counts-comparison': 'Import & Export - Count Comparison',
  'imp-exp-nominal-earnings': 'Import & Export - Earnings by Divisions for Nominal Payment',
  'imp-exp-nominal-count': 'Import & Export - Task Counts by Division for Nominal Payment',
  'imp-exp-levels-by-qty': 'Import & Export - L1 & L2 Levels by Quantity per Department',
  'consignment-by-country': 'Import & Export - Consignments by Country Report',
  'imp-exp-tonnage': 'Import & Export - Tonnage Report',
  'h-form': 'Finance - H Form Report',
  'summary-report': 'Summary Report',
  'cashier-point-summary': 'Finance - Earnings & Invoice Count Settled by Cashier Point',
};

const reportComponentMap: Record<
  string,
  React.ComponentType<{ reportKey: string; filters: any; onRowClick?: (record: any) => void }>
> = {
  // Lab - Expandable
  'earnings-by-divisions': LabReportTable,
  'test-count-by-divisions': LabReportTable,
  'earnings-by-divisions-nom-pay': LabReportTable,
  'test-count-by-divisions-nom-pay': LabReportTable,

  // Lab - Flat
  'sample-count-by-divisions': LabFlatReportTable,
  'item-qty-by-divisions': LabFlatReportTable,

  // PR & License
  'pr-earnings-by-departments': PRLicenseReportTable,
  'pr-count-by-departments': PRLicenseReportTable,
  'registrations-by-establishment': PREstablishmentReportTable,

  // Import & Export
  'imp-exp-earnings-by-departments': ImportExportReportTable,
  'imp-exp-count-by-departments': ImportExportReportTable,
  'imp-exp-levels-by-qty': ImpExpLevelQtyReport,
  'consignment-by-country': ConsignmentByCountryReport,
  'imp-exp-tonnage': TonnageReport,

  // Import & Export Comparison
  'imp-exp-earnings-comparison': ImportExportComparisonLayout,
  'imp-exp-counts-comparison': ImportExportComparisonLayout,

  // Import & Export Nominal
  'imp-exp-nominal-earnings': ImportExportNominalEarnings,
  'imp-exp-nominal-count': ImportExportNominalCount,

  // Finance
  'h-form': HReport,
  'summary-report': SummaryReport,
  'cashier-point-summary': CPSummaryReport,
};

// Optional: Track which reports need onRowClick prop
const reportsWithRowClick = new Set(['registrations-by-establishment']);

const ReportPage: React.FC = () => {
  const location = useLocation();
  const { setBreadcrumb } = useHeader();
  const navigate = useNavigate();
  const reportKey = location.state?.reportKey || 'registrations-by-establishment';
  const repType = location.state?.repType || (localStorage.getItem('selected_rep_type') as string) || 'lab';
  const initialFilters = location.state?.filters || {};

  console.log('Passed Report Key: ', reportKey);

  const currentBreadcrumbName = titleMap[reportKey] || 'Report';
  const [currentFilters, setCurrentFilters] = useState(initialFilters);
  const [isEditMode, setIsEditMode] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);
  const prCategoryId = location.state?.pr_category_id;

  useEffect(() => {
    const parent = breadcrumbMap[repType] || { path: '/', name: 'Reports' };
    const routes = [
      { path: parent.path, breadcrumbName: parent.name },
      { path: '/report', breadcrumbName: currentBreadcrumbName },
    ];

    setBreadcrumb(<BaseBreadcrumbWithRender routes={routes} separator=">" />);
    return () => setBreadcrumb(undefined);
  }, [repType, currentBreadcrumbName, setBreadcrumb]);

  const handleFiltersChange = (newFilters: any) => {
    setCurrentFilters(newFilters);
  };

  const addPDFStyles = () => {
    const style = document.createElement('style');
    style.id = 'pdf-styles';
    style.innerHTML = `
    /* Hide elements in PDF */
    .hide-in-pdf, .ant-table-row-expand-icon {
      display: none !important;
    }
  `;
    document.head.appendChild(style);
  };

  const removePDFStyles = () => {
    const style = document.getElementById('pdf-styles');
    if (style) style.remove();
  };

  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;

    addPDFStyles();

    // Hide elements that should not appear in PDF
    const elementsToHide = reportRef.current.querySelectorAll('.hide-in-pdf');
    elementsToHide.forEach((el) => ((el as HTMLElement).style.display = 'none'));

    try {
      // Capture the component
      const canvas = await html2canvas(reportRef.current, {
        scale: 2, // higher resolution
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 8; // page margin in mm

      // Canvas dimensions in px
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      // PDF dimensions in px (using 1mm ~ 3.78px)
      const pdfPageHeightPx = ((pageHeight - margin * 2) * canvasWidth) / (pageWidth - margin * 2);

      let remainingHeight = canvasHeight;
      let position = 0;

      while (remainingHeight > 0) {
        const canvasChunk = document.createElement('canvas');
        canvasChunk.width = canvas.width;
        canvasChunk.height = Math.min(pdfPageHeightPx, remainingHeight);

        const ctx = canvasChunk.getContext('2d');
        if (ctx) {
          ctx.drawImage(canvas, 0, position, canvas.width, canvasChunk.height, 0, 0, canvas.width, canvasChunk.height);
        }

        const imgChunkData = canvasChunk.toDataURL('image/png');
        const pdfHeight = (canvasChunk.height * (pageWidth - margin * 2)) / canvas.width;

        if (position > 0) pdf.addPage();
        pdf.addImage(imgChunkData, 'PNG', margin, margin, pageWidth - margin * 2, pdfHeight);

        remainingHeight -= canvasChunk.height;
        position += canvasChunk.height;
      }

      pdf.save(`${currentBreadcrumbName}.pdf`);
    } catch (err) {
      console.error(err);
      message.error('Failed to generate PDF.');
    } finally {
      // Restore hidden elements
      elementsToHide.forEach((el) => ((el as HTMLElement).style.display = ''));
      removePDFStyles();
    }
  };

  const handleConvertToCSV = () => {
    if (!reportRef.current) {
      message.error('Nothing to export.');
      return;
    }

    try {
      convertReportToCSV(reportRef.current, currentBreadcrumbName);
    } catch (err) {
      console.error(err);
      message.error('CSV generation failed.');
    }
  };

  const handleEditFilters = () => {
    setIsEditMode(true);
  };

  const handleRowClick = (record: any) => {
    console.log('Row clicked:', record);
    navigate(`/report/detailed`, {
      state: {
        reportKey,
        repType,
        filters: currentFilters,
        location_name: record.location_name,
        id: record.location_id,
      },
    });
  };

  if (!reportKey) return <div>No report key provided.</div>;

  const ReportComponent = reportComponentMap[reportKey];

  if (!ReportComponent) {
    return <div>No matching component found for report key: {reportKey}</div>;
  }

  // Determine if this report needs the onRowClick handler
  const componentProps: any = {
    reportKey,
    filters: currentFilters,
  };

  if (reportsWithRowClick.has(reportKey)) {
    componentProps.onRowClick = handleRowClick;
  }

  return (
    <div>
      <PageTitle>{currentBreadcrumbName}</PageTitle>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          {!isEditMode && (
            <>
              <BaseButton icon={<DownloadOutlined />} onClick={handleDownloadPDF} type="primary" size="small">
                Download as PDF
              </BaseButton>
              <BaseButton icon={<FileExcelOutlined />} onClick={handleConvertToCSV} type="primary" size="small">
                Convert to CSV
              </BaseButton>
            </>
          )}
        </Space>
        <BaseButton
          type="primary"
          icon={<EditOutlined />}
          onClick={handleEditFilters}
          disabled={isEditMode}
          size="small"
        >
          Edit Filters
        </BaseButton>
      </div>

      <div ref={reportRef}>
        <ReportFilterBar
          reportKey={reportKey}
          initialFilters={currentFilters}
          onFiltersChange={handleFiltersChange}
          isEditMode={isEditMode}
          onEditModeChange={setIsEditMode}
        />
        <br />

        <ReportComponent {...componentProps} />
      </div>
    </div>
  );
};

export default ReportPage;
